import { Box, Button, CircularProgress, Dialog, DialogActions, Divider, Stack, Typography } from "@mui/material";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { inputTypes, selectOptionsKeyName, statusOptions } from "data/globalData";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	sourcingOptionCogsColumn,
	sourcingOptionCogsRemarkColumn,
	sourcingOnlyColorColumnKeys,
	buildColorTableColumns,
	fabricCodeSearchColumn,
	finalMRPColumn,
	hsnCodeColumn,
	fwMaterialCodeSearchColumn,
	washCareColumn,
} from "./data";
import { fabricCategoryColumn, fabricFinalColumns } from "../../FabricMaster/data";
import axios from "axios";
import { PimDashboardState } from "context/PimDashboardContext";
import RenderAttributeInputs from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderAttributes/RenderAttributeInputs";
import { attrGrpAttrAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";
import {
	attrConditionalDisplayColObj,
	attrGroupLinkedTables,
	formatColumnValue,
	publishTableColumn,
	styleColorAssocObj,
} from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { TableImgComponent } from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/ColorsSection/ColorsSection";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";
import { footwearMaterialColumns, upperMaterialColumn } from "../../FootwearMaterialMaster/data";
import RenderConditionalAttributeInputs from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderConditionalAttributes/RenderConditionalAttributeInputs";
import { SOURCING_STYLE_SHEET_API } from "../data";

const EditFabricStyleDetails = ({
	modal,
	setModal,
	selectedStyle,
	setSelectedStyle,
	saveCallback,
	canEdit = true,
	canPublish = false,
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen, Loadingopen } = GlobalState();
	const { allAttributes, attrGroupsWithAttr } = PimDashboardState();

	const [fullStyleData, setFullStyleData] = useState();
	const [updatedFabriDetails, setUpdatedFabricDetails] = useState({});
	const [searchObj, setSearchObj] = useState();
	const [loading, setLoading] = useState(true);
	const [colors, setColors] = useState(true);

	const [colorTableColumns, setColorTableColumns] = useState([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [rejectingColorId, setRejectingColorId] = useState(null);
	const [colorToReject, setColorToReject] = useState(null);

	const publishedColorCount = useMemo(
		() => (Array.isArray(colors) ? colors.filter((row) => row?.status === statusOptions.PUBLISHED.key).length : 0),
		[colors],
	);

	const canRejectColor = (row) =>
		canEdit &&
		row?.status === statusOptions.PUBLISHED.key &&
		row?.status !== statusOptions.DESIGNER_PENDING.key &&
		publishedColorCount > 1;

	const masterDerivedColumnKeys = useMemo(
		() => [
			fabricCategoryColumn.key,
			...Object.values(fabricFinalColumns).map((col) => col.key),
			upperMaterialColumn.key,
			...Object.values(footwearMaterialColumns).map((col) => col.key),
		],
		[],
	);

	const fabricDisabledColumns = useMemo(() => {
		const disabled = new Set(masterDerivedColumnKeys);
		if (!isEditMode) {
			allAttributes
				.filter(
					(row) =>
						row.group_id === "fabric_details" ||
						row.key === hsnCodeColumn.key ||
						row.key === finalMRPColumn.key ||
						row.key === washCareColumn.key ||
						row.key === "sizes",
				)
				.forEach((row) => disabled.add(row.key));
		}
		return [...disabled, finalMRPColumn.key];
	}, [allAttributes, isEditMode, masterDerivedColumnKeys]);

	const toReadOnlyColumns = (columns) =>
		columns.map((col) => {
			if (!isEditMode || masterDerivedColumnKeys.includes(col.key)) {
				return { ...col, input_type: inputTypes.AUTO_GENERATE.key };
			}
			return col;
		});

	const noopSetData = useCallback(() => {}, []);

	const handleModalClose = () => {
		setIsEditMode(false);
		setModal(false);
		setSelectedStyle();
	};

	const handleCancelEdit = async () => {
		setIsEditMode(false);
		setLoading(true);
		await fetchStyleDetails();
	};

	const handleRejectColor = async (colorRow) => {
		if (!canRejectColor(colorRow)) return;

		try {
			setRejectingColorId(colorRow.id);
			setLoadingOpen(true);
			await axios.put(`${SOURCING_STYLE_SHEET_API}/reject_color_by_sourcing`, {
				style_id: selectedStyle?.style_id,
				color_id: colorRow.id,
			});
			setColors((prev) => prev.map((row) => (row.id === colorRow.id ? { ...row, status: statusOptions.REJECTED.key } : row)));
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "success", message: "Color rejected successfully" });
			if (typeof saveCallback === "function") saveCallback(selectedStyle);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setRejectingColorId(null);
			setColorToReject(null);
			setLoadingOpen(false);
		}
	};

	const handleRejectClick = (colorRow) => {
		if (!canRejectColor(colorRow)) return;
		setColorToReject(colorRow);
	};

	const fetchFabricMasterdataFromSearch = async (offset = 0, limit = 20, searchTerm = "") => {
		const query = {
			limit,
			offset,
		};
		if (searchTerm) query["search"] = searchTerm;
		// construct your request logic
		const res = await axios.get(`${SOURCING_STYLE_SHEET_API}/get_fabric_data_by_search`, {
			params: query,
			query: {},
		});

		return res;
	};

	const fetchFootwearMaterialFromSearch = async (offset = 0, limit = 20, searchTerm = "") => {
		const query = {
			limit,
			offset,
		};
		if (searchTerm) query["search"] = searchTerm;
		const res = await axios.get(`${SOURCING_STYLE_SHEET_API}/get_footwear_material_data_by_search`, {
			params: query,
			query: {},
		});

		return res;
	};

	const handleFabricCodeSelect = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(searchObj);

		if (!dataObj || !dataObj[fabricCodeSearchColumn.key]) {
			setSearchObj(dataObj);
			return;
		}
		let updatedFabricValues = [fabricCategoryColumn, ...Object.values(fabricFinalColumns)].reduce((acc, curr) => {
			acc[curr.key] = dataObj[fabricCodeSearchColumn.key][curr.key];
			return acc;
		}, {});
		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};

	const handleFootwearMaterialSelect = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(searchObj);
		if (!dataObj || !dataObj[fwMaterialCodeSearchColumn.key]) {
			setSearchObj(dataObj);
			return;
		}
		let updatedFabricValues = [upperMaterialColumn, ...Object.values(footwearMaterialColumns)].reduce((acc, curr) => {
			acc[curr.key] = dataObj[fwMaterialCodeSearchColumn.key][curr.key];
			return acc;
		}, {});
		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};
	const handleFabricInputChange = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(fullStyleData);

		let updatedFabricValues = allAttributes
			.filter(
				(item) =>
					item.group_id === "fabric_details" &&
					!fabricFinalColumns[item.key] &&
					item.key !== fabricCategoryColumn.key &&
					!footwearMaterialColumns[item.key] &&
					item.key !== upperMaterialColumn.key,
			)
			.reduce((acc, curr) => {
				acc[curr.key] = dataObj[attrGroupLinkedTables.style_details.key][curr.key];
				return acc;
			}, {});

		setUpdatedFabricDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
		setSearchObj(dataObj);
	};
	const handleFabricSaveClick = async (publish = false) => {
		if (publish) {
			const isFootwear = fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "FOOT WEAR";
			const isFragrance = fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "PERSONAL CARE";
			const isBag =
				fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "ADDONS" &&
				["TROLLEY BAG", "BACK PACK", "BAG"].includes(fullStyleData?.[attrGroupLinkedTables.style.key]?.category);
			if (isFragrance || isBag) {
			} else if (isFootwear) {
				// Check footwear material columns
				const missingCols = Object.values(footwearMaterialColumns).filter(
					(col) => !fullStyleData[attrGroupLinkedTables.style_details.key]?.[col.key],
				);
				if (missingCols.length) {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: `Please Fill the values for ${missingCols.map((row) => row.name).join(", ")}.`,
					});
					return;
				}
			} else {
				// Check fabric columns
				if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.fabric_category) {
					setSnackBarOpen(true);
					setSnackBar({ ...snackBar, severity: "error", message: "Please Select Fabric Code First" });
					return;
				}

				const missingCols = [...Object.values(fabricFinalColumns)].filter(
					(col) => !fullStyleData[attrGroupLinkedTables.style_details.key]?.[col.key],
				);
				if (missingCols.length) {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: `Please Fill the values for ${missingCols.map((row) => row.name).join(", ")}.`,
					});
					return;
				}
			}

			if (!fullStyleData[attrGroupLinkedTables.style_details.key]?.[washCareColumn.key]) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: "Please Add Wash Care Instructions" });
				return;
			}

			const hsnCode = String(fullStyleData[attrGroupLinkedTables.style_details.key]?.[hsnCodeColumn.key] ?? "").trim();
			if (!hsnCode) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: "Please Add HSN Code" });
				return;
			}
			if (!/^\d{8}$/.test(hsnCode)) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: "HSN Code must be a number and exactly 8 digits",
				});
				return;
			}

			// colors pending
			let colorsPending = [];
			colors?.forEach((row) => {
				if (row?.status !== statusOptions.REJECTED.key)
					if (!+row[sourcingOptionCogsColumn.key] > 0)
						colorsPending.push({ name: `${row.color}-${row.pallete}`, key: `${row.color}-${row.pallete}` });
			});
			if (colorsPending.length) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: `Please Fill the ${sourcingOptionCogsColumn.name} for ${colorsPending.map((row) => row.name).join(",")}`,
				});
				return;
			}

			const isCogsRemarkEmpty = (row) => !`${row?.[sourcingOptionCogsRemarkColumn.key] ?? ""}`.trim();
			const remarksPending = (colors || []).filter(
				(row) => row?.status !== statusOptions.REJECTED.key && isCogsRemarkEmpty(row),
			);
			if (remarksPending.length) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: `Please Fill the ${sourcingOptionCogsRemarkColumn.name} for ${remarksPending
						.map((row) => `${row.color}-${row.pallete}`)
						.join(",")}`,
				});
				return;
			}
		}

		const body = {
			...updatedFabriDetails,
			style_id: selectedStyle?.style_id,
			id: fullStyleData[attrGroupLinkedTables.style_details.key]?.id,
			[styleColorAssocObj]: colors
				.filter((row) => {
					if (row?.status === statusOptions.REJECTED.key) return false;
					if (publish) return true;
					return row.is_updated === true;
				})
				.map((row) => ({
					id: row?.id,
					[sourcingOptionCogsColumn.key]: row[sourcingOptionCogsColumn.key],
					[sourcingOptionCogsRemarkColumn.key]: row[sourcingOptionCogsRemarkColumn.key] ?? "",
				})),
		};
		try {
			setLoadingOpen(true);
			await axios.put(`${SOURCING_STYLE_SHEET_API}/update_style_fabric${publish ? "?publish=true" : ""}`, body).then((res) => {
				saveCallback({
					...selectedStyle,
					...body,
					...(res.data?.style ?? {}),
				});
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "success", message: "Data updated Successfully" });
				handleModalClose();
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
		}
	};

	const fetchStyleDetails = async () => {
		try {
			const res = await axios.get(`${SOURCING_STYLE_SHEET_API}/style_by_id/${selectedStyle?.style_id}`);
			setFullStyleData(res.data);
			setUpdatedFabricDetails(
				allAttributes
					.filter((attr) => attr.group_id === "fabric_details")
					.reduce((acc, row) => {
						acc[row.key] = res.data?.[attrGroupLinkedTables.style_details.key]?.[row.key];
						return acc;
					}, {}),
			);
			setColors(res.data?.[attrGroupLinkedTables.color.key]);
			setColorTableColumns(buildColorTableColumns(allAttributes, attrGroupsWithAttr));
			setLoading(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
		}
	};

	const handleColorCogsChange = (newRow, oldRow) => {
		if (newRow?.status === statusOptions.REJECTED.key || !isEditMode) {
			return oldRow;
		}
		const cogsVal = newRow[sourcingOptionCogsColumn.key];
		const cogsOk = cogsVal === "" || cogsVal == null || !isNaN(Number(cogsVal));
		if (!cogsOk) return oldRow;
		setColors((prev) =>
			prev.map((item) =>
				item.id === newRow.id
					? {
							...item,
							[sourcingOptionCogsColumn.key]: newRow[sourcingOptionCogsColumn.key],
							[sourcingOptionCogsRemarkColumn.key]: newRow[sourcingOptionCogsRemarkColumn.key],
							is_updated: true,
					  }
					: item,
			),
		);
		return newRow;
	};

	useEffect(() => {
		if (modal) setIsEditMode(false);
	}, [modal, selectedStyle?.style_id]);

	useEffect(() => {
		if (selectedStyle) fetchStyleDetails();
	}, [modal]);

	return (
		<Dialog
			fullWidth
			maxWidth="xl"
			open={modal}
			onClose={() => handleModalClose()}
			scroll="paper"
			keepMounted={false}
			PaperProps={{
				sx: {
					height: "90vh",
					maxHeight: "90vh",
					borderRadius: 2,
					overflow: "hidden",
					bgcolor: grey[50],
					display: "flex",
					flexDirection: "column",
				},
			}}>
			{loading ? (
				<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" />
				</Box>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						flexWrap="wrap"
						gap={1}
						sx={{
							px: 2.5,
							py: 1.5,
							bgcolor: "#fff",
							borderBottom: `1px solid ${grey[200]}`,
							flexShrink: 0,
						}}>
						<Typography variant="subtitle1" fontWeight={700}>
							{isEditMode ? "Edit Fabric Details" : "Fabric Style Details"}
						</Typography>
						<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
							{isEditMode ? (
								<>
									<Button variant="outlined" size="small" onClick={() => handleCancelEdit()}>
										Cancel
									</Button>
									{canEdit && (
										<Button variant="contained" size="small" onClick={() => handleFabricSaveClick(false)}>
											Save
										</Button>
									)}
									{fullStyleData?.[attrGroupLinkedTables.style.key]?.status === statusOptions.PUBLISHED.key ? (
										<Button
											variant="contained"
											color="success"
											size="small"
											disableFocusRipple
											disableRipple
											disableElevation>
											Published
										</Button>
									) : (
										canEdit &&
										canPublish && (
											<Button
												variant="contained"
												color="success"
												size="small"
												onClick={() => handleFabricSaveClick(true)}
												disabled={
													fullStyleData?.[attrGroupLinkedTables.style.key]?.status !==
													statusOptions.SOURCING_PENDING.key
												}>
												Save And Publish
											</Button>
										)
									)}
								</>
							) : (
								<>
									<Button variant="outlined" size="small" onClick={() => handleModalClose()}>
										Close
									</Button>
									{canEdit && (
										<Button variant="contained" size="small" onClick={() => setIsEditMode(true)}>
											Edit
										</Button>
									)}
								</>
							)}
						</Box>
					</Box>
					<Box display="flex" width="100%" flex={1} minHeight={0} overflow="hidden">
						<Stack
							width="65%"
							flex={1}
							p={2}
							spacing={1.5}
							boxSizing="border-box"
							sx={{ overflowY: "auto" }}>
							<Box
								sx={{
									bgcolor: "#fff",
									border: `1px solid ${grey[200]}`,
									borderRadius: 1.5,
									p: 1.5,
								}}>
							{fullStyleData?.[attrGroupLinkedTables.style.key]?.division === "FOOT WEAR" ? (
								<SearchInputFieldWithDebounce
									column={fwMaterialCodeSearchColumn}
									data={searchObj}
									setData={handleFootwearMaterialSelect}
									fetchData={fetchFootwearMaterialFromSearch}
									selectOptKeyName={selectOptionsKeyName}
									disableLabel
									storeValueAs="object"
									width="300px"
									disabled={!isEditMode}
								/>
							) : (
								<SearchInputFieldWithDebounce
									column={fabricCodeSearchColumn}
									data={searchObj}
									setData={handleFabricCodeSelect}
									fetchData={fetchFabricMasterdataFromSearch}
									selectOptKeyName={selectOptionsKeyName}
									disableLabel
									storeValueAs="object"
									width="300px"
									disabled={!isEditMode}
								/>
							)}
							{/* fabric Details */}
							<Box sx={{ width: "100%" }}>
								{Object.keys(attrGroupsWithAttr)
									.reduce((acc, row) => {
										if (attrGroupsWithAttr[row]?.group_id === "fabric_details") acc.push(attrGroupsWithAttr[row]);
										return acc;
									}, [])
									?.map((row) => {
										return (
											<Box width="100%">
												{row?.data?.[attrConditionalDisplayColObj.key] ? (
													<RenderConditionalAttributeInputs
														columns={toReadOnlyColumns([
															...row[attrGrpAttrAssocObj]?.filter(
																(row) =>
																	row.key !== finalMRPColumn.key &&
																	row.key !== hsnCodeColumn.key &&
																	row.key !== washCareColumn.key &&
																	row.key !== "sizes",
															),
														])}
														setData={isEditMode ? handleFabricInputChange : noopSetData}
														data={fullStyleData}
														attrGrp={row}
														itemWidth="32%"
														width={"100%"}
														setErrorObj={() => {}}
														errorObj={{}}
													/>
												) : (
													<RenderAttributeInputs
														columns={[
															...row[attrGrpAttrAssocObj]?.filter(
																(row) =>
																	row.key !== finalMRPColumn.key &&
																	row.key !== hsnCodeColumn.key &&
																	row.key !== washCareColumn.key &&
																	row.key !== "sizes",
															),
														]}
														setData={isEditMode ? handleFabricInputChange : noopSetData}
														data={fullStyleData}
														attrGrp={row}
														itemWidth="32%"
														width={"100%"}
														setErrorObj={() => {}}
														errObj={{}}
														disabledColumns={fabricDisabledColumns}
													/>
												)}
											</Box>
										);
									})}
							</Box>

							<Box sx={{ width: "100%" }}>
								<RenderAttributeInputs
									columns={toReadOnlyColumns([
										...(allAttributes.some((row) => row?.key === washCareColumn.key)
											? allAttributes.reduce((acc, row) => {
													if (row?.key === washCareColumn.key) acc.push({ ...washCareColumn, ...row });
													return acc;
												}, [])
											: [washCareColumn]),
										...allAttributes.reduce((acc, row) => {
											if (row?.key === "sizes") acc.push({ ...row, input_type: inputTypes.AUTO_GENERATE.key });
											return acc;
										}, []),
									])}
									setData={isEditMode ? handleFabricInputChange : noopSetData}
									data={fullStyleData}
									attrGrp={attrGroupsWithAttr["fabric_details"]}
									itemWidth="32%"
									width={"100%"}
									setErrorObj={() => {}}
									errObj={{}}
									disabledColumns={fabricDisabledColumns}
								/>
							</Box>

							{/* MRP */}
							<Box sx={{ width: "100%" }}>
								<RenderAttributeInputs
									columns={toReadOnlyColumns([
										...allAttributes.reduce((acc, row) => {
											if (row?.key === hsnCodeColumn.key) acc.push({ ...row, ...hsnCodeColumn });
											if (row?.key === finalMRPColumn.key) acc.push({ ...row, ...finalMRPColumn });
											return acc;
										}, []),
									])}
									setData={isEditMode ? handleFabricInputChange : noopSetData}
									data={fullStyleData}
									attrGrp={attrGroupsWithAttr["fabric_details"]}
									itemWidth="32%"
									width={"100%"}
									setErrorObj={() => {}}
									errObj={{}}
									disabledColumns={fabricDisabledColumns}
								/>
							</Box>
							</Box>

							{/* color section */}
							<Box
								sx={{
									bgcolor: "#fff",
									border: `1px solid ${grey[200]}`,
									borderRadius: 1.5,
									p: 1.5,
								}}>
								<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: grey[800] }}>
									COGS
								</Typography>
							{colorTableColumns?.length >= 0 && (
								<Box sx={{ height: 290, width: "100%", minHeight: 0, borderRadius: 1, overflow: "hidden", border: `1px solid ${grey[200]}` }}>
									<DataGrid
										rows={colors ? colors : []}
										columns={[
											{
												headerName: "Image",
												field: "img",
												minWidth: 120,
												headerAlign: "center",
												flex: 1,
												cellClassName: "readonly-cell",
												headerClassName: "readonly-header",
												renderCell: ({ row }) => {
													return <TableImgComponent data={row} column="url" />;
												},
											},
											...colorTableColumns?.map((colorCol) => {
												const isCogsCol = sourcingOnlyColorColumnKeys.includes(colorCol?.key);
												return {
													field: colorCol?.key,
													headerName: colorCol?.name?.toUpperCase(),
													headerAlign: "center",
													align: "center",
													flex: 1,
													...colorCol,
													editable: isEditMode && isCogsCol,
													minWidth: colorCol?.key === sourcingOptionCogsRemarkColumn.key ? 160 : 100,
													cellClassName: isCogsCol ? "cogs-edit-cell" : "readonly-cell",
													headerClassName: isCogsCol ? "cogs-edit-header" : "readonly-header",
												};
											}),
											{
												...publishTableColumn(null, true),
												cellClassName: "readonly-cell",
												headerClassName: "readonly-header",
											},
											...(!isEditMode
												? [
														{
															field: "action",
															headerName: "Action",
															minWidth: 110,
															headerAlign: "center",
															align: "center",
															flex: 1,
															sortable: false,
															filterable: false,
															cellClassName: "readonly-cell",
															headerClassName: "readonly-header",
															renderCell: ({ row }) => (
																<Button
																	size="small"
																	color="error"
																	variant="outlined"
																	disabled={!canRejectColor(row) || rejectingColorId === row.id}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleRejectClick(row);
																	}}>
																	Reject
																</Button>
															),
														},
													]
												: []),
										]}
										sx={{
											height: 290,
											border: "none",
											"--DataGrid-containerBackground": grey[100],
											"& .MuiDataGrid-main": { overflow: "auto" },
											"& .MuiDataGrid-virtualScroller": { overflow: "auto !important" },
											"& .MuiDataGrid-columnHeaders": {
												backgroundColor: grey[100],
												borderBottom: `1px solid ${grey[300]}`,
											},
											"& .MuiDataGrid-row": {
												borderBottom: `1px solid ${grey[200]}`,
												cursor: "pointer",
												backgroundColor: grey[100],
											},
											"& .MuiDataGrid-row:hover": {
												backgroundColor: grey[200],
											},
											"& .MuiDataGrid-row.rejected-color-row": {
												backgroundColor: grey[200],
												opacity: 0.75,
												cursor: "default",
											},
											"& .MuiDataGrid-columnHeader": {
												cursor: "pointer",
												borderRight: `1px solid ${grey[200]}`,
												backgroundColor: grey[100],
											},
											"& .MuiDataGrid-cell": {
												borderRight: `1px solid ${grey[200]}`,
												backgroundColor: grey[100],
											},
											"& .readonly-header": {
												backgroundColor: `${grey[100]} !important`,
											},
											"& .readonly-cell": {
												backgroundColor: `${grey[100]} !important`,
											},
											"& .cogs-edit-header": {
												backgroundColor: "#fff !important",
												fontWeight: 700,
											},
											"& .cogs-edit-cell": {
												backgroundColor: "#fff !important",
											},
											"& .MuiDataGrid-row.rejected-color-row .cogs-edit-cell": {
												backgroundColor: `${grey[100]} !important`,
											},
											"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
												outline: "none",
											},
										}}
										columnHeaderHeight={35}
										rowHeight={60}
										disableRowSelectionOnClick
										pageSizeOptions={[5, 10]}
										hideFooter
										pagination={false}
										disableAutosize
										disableVirtualization
										getRowClassName={(params) =>
											params.row?.status === statusOptions.REJECTED.key ? "rejected-color-row" : ""
										}
										isCellEditable={({ row, field }) =>
											isEditMode &&
											sourcingOnlyColorColumnKeys.includes(field) &&
											row?.status !== statusOptions.REJECTED.key
										}
										processRowUpdate={isEditMode ? handleColorCogsChange : undefined}
									/>
								</Box>
							)}
							</Box>
						</Stack>
						<Divider flexItem orientation="vertical" />
						<Stack
							width="30%"
							maxWidth={"320px"}
							sx={{
								p: 2,
								overflowY: "auto",
								bgcolor: "#fff",
								borderLeft: `1px solid ${grey[200]}`,
							}}>
							<Typography variant="subtitle2" fontWeight={700} sx={{ width: "100%", mb: 1, color: grey[800] }}>
								Style Details
							</Typography>
							{allAttributes
								.filter((row) => row.linked_table === attrGroupLinkedTables.style.key)
								.map((row) => {
									return (
										<Box
											width={"100%"}
											key={row?.name}
											display="flex"
											sx={{ border: `1px solid ${grey[200]}`, borderBottom: "none", "&:last-of-type": { borderBottom: `1px solid ${grey[200]}` } }}
											boxSizing="border-box">
											<Typography
												variant="caption"
												sx={{
													width: "40%",
													p: "6px 8px",
													background: grey[100],
													color: grey[700],
													overflow: "hidden",
													fontWeight: 600,
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="caption"
												sx={{
													width: "60%",
													p: "6px 8px",
													background: "#fff",
													fontWeight: 400,
													overflow: "hidden",
													color: grey[900],
												}}>
												{fullStyleData?.[row.linked_table][row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
							<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1, color: grey[800] }}>
								Suggestions
							</Typography>
							{allAttributes
								.filter((row) => row.group_id === "suggestions")
								.map((row) => {
									return (
										<Box
											width={"100%"}
											key={row?.name}
											display="flex"
											sx={{ border: `1px solid ${grey[200]}`, borderBottom: "none", "&:last-of-type": { borderBottom: `1px solid ${grey[200]}` } }}
											boxSizing="border-box">
											<Typography
												variant="caption"
												sx={{
													width: "40%",
													p: "6px 8px",
													background: grey[100],
													color: grey[700],
													overflow: "hidden",
													fontWeight: 600,
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="caption"
												sx={{
													width: "60%",
													p: "6px 8px",
													background: "#fff",
													fontWeight: 400,
													overflow: "hidden",
													color: grey[900],
												}}>
												{fullStyleData?.[row.linked_table][row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
						</Stack>
					</Box>

					<Dialog fullWidth maxWidth="xs" open={Boolean(colorToReject)} onClose={() => setColorToReject(null)}>
						<Box p={2}>
							<Typography variant="h6" gutterBottom>
								Reject color?
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Are you sure you want to reject{" "}
								<strong>
									{colorToReject?.color}
									{colorToReject?.pallete ? ` - ${colorToReject.pallete}` : ""}
								</strong>
								? At least one published color must remain on this style.
							</Typography>
						</Box>
						<DialogActions sx={{ px: 2, pb: 2 }}>
							<Button variant="outlined" onClick={() => setColorToReject(null)}>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="error"
								disabled={rejectingColorId === colorToReject?.id}
								onClick={() => colorToReject && handleRejectColor(colorToReject)}>
								Confirm Reject
							</Button>
						</DialogActions>
					</Dialog>
				</Box>
			)}
		</Dialog>
	);
};

export default EditFabricStyleDetails;
